import { ComponentType, ReactNode } from 'react';

import styled from 'styled-components';

import { BadgePremium } from '@components/badge-premium';
import { EmptySearchState } from '@components/empty-search-state';
import {
  IconContainerVariant,
  IconFancyContainer,
} from '@components/icon-fancy-container/icon-fancy-container';
import { IconProps } from '@components/icons/base';
import { IconDropdown } from '@components/icons/dropdown';
import { Modal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { BASE_ACTION_TYPE, JourneyActionValuesTypes } from 'api/types';
import { Values } from 'ui/journey-builder/node-types/types';

type ItemIconProps = {
  icon: ComponentType<IconProps>;
  variant: IconContainerVariant;
};

export const ItemIcon = ({ icon: Icon, variant }: ItemIconProps) => (
  <IconFancyContainer icon={<Icon size={16} />} variant={variant} size="sm" />
);

export type OnCreateActionSelectProps = { actionType: BASE_ACTION_TYPE; values?: Values };

export type ActionItemProps<T extends BASE_ACTION_TYPE = BASE_ACTION_TYPE> = {
  text: string | ReactNode;
  icon?: JSX.Element;
  type: T;
  values?: JourneyActionValuesTypes[T];
  onSelect?(): any;
  isPremium?: boolean;
  description?: string;
};

export type ActionGroup = {
  type: string;
  actions: ActionItemProps[];
};

export const TabContentWrapper = styled(Modal.Content)({
  padding: `${pxToRem(16)} ${pxToRem(40)}`,
  height: `min(${pxToRem(548)}, calc(100vh - ${pxToRem(196)}))`,
  overflow: 'auto',
});

export const ActionItem = ({ onSelect, icon, text, isPremium, description }: ActionItemProps) => (
  <ActionItemContainer onClick={onSelect} data-disabled={isPremium} className="action-item">
    <Stack orientation="horizontal" align="center" justify="space-between">
      <ActionItemStack data-disabled={isPremium}>
        {icon}
        <ActionItemTextWrapper>
          {typeof text === 'string' ? <Text text={text} font="semibold14" ellipsis /> : text}
          <Text text={description} font="regular12" color="textSecondary" ellipsis />
        </ActionItemTextWrapper>
      </ActionItemStack>
      {isPremium && <BadgePremium />}
    </Stack>
  </ActionItemContainer>
);

const ActionItemTextWrapper = styled.div({
  overflow: 'hidden',
  minWidth: 0,
});

export const ActionItemContainer = styled.button<{ $hasDescription?: boolean }>({
  width: '100%',
  borderRadius: pxToRem(4),
  padding: pxToRem(4),
  lineHeight: pxToRem(20),
  minHeight: pxToRem(32),
  background: 'transparent',
  border: 'none',
  textAlign: 'left',
  '&:hover': {
    backgroundColor: theme.color.uiTrans5,
    cursor: 'pointer',
  },
  '&[data-disabled="true"]': {
    cursor: 'not-allowed',
  },
});

export const ActionItemStack = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  overflow: 'hidden',
  minWidth: 0,
  '&[data-disabled="true"]': {
    opacity: 0.5,
  },
});

const GroupHeaderContainer = styled(ActionItemContainer)({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
});

type GroupHeaderProps = {
  onClick: () => void;
  isCollapsed: boolean;
  text: string;
  textFont?: 'semibold12' | 'semibold14';
  icon?: ReactNode;
};

export const GroupHeader = ({
  onClick,
  isCollapsed,
  text,
  textFont = 'semibold12',
  icon,
}: GroupHeaderProps) => (
  <GroupHeaderContainer onClick={onClick}>
    {icon}
    <Stack orientation="horizontal" space={1} align="center">
      <Text text={text} font={textFont} color="textSecondary" />
      <IconDropdown rotateDegrees={isCollapsed ? -90 : 0} color="buttonIconPositive" />
    </Stack>
  </GroupHeaderContainer>
);

type SearchEmptyStateProps = {
  onClearSearch: () => void;
};

export const SearchEmptyState = ({ onClearSearch }: SearchEmptyStateProps) => {
  return <EmptySearchState variant="small" skipMargin onClearSearch={onClearSearch} />;
};

export const ActionsList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(4),
  marginLeft: pxToRem(8),
  paddingLeft: pxToRem(20),
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: pxToRem(8),
    bottom: pxToRem(8),
    width: pxToRem(1),
    marginLeft: pxToRem(12),
    backgroundColor: theme.color.uiTrans20,
  },
});
